import React from "react"
import moment from "moment"

// Shared by BankStatementConsole and CreditCardStatementConsole.

export const formatInr = n =>
    `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatDay = d => (d && moment(d).isValid() ? moment(d).format("DD-MM-YYYY") : null)

// Per-account figures from the summary/by-bank | by-card endpoint, keyed by
// the account's lower-cased label.
export const toAccountCounts = r => ({
    total: r.total || 0,
    matched: r.matched || 0,
    unmatched: r.unmatched || 0,
    last_uploaded_at: r.last_uploaded_at || null,
    last_statement_date: r.last_statement_date || null,
})

// Account dropdown option: label, last upload date (menu only — the selected
// value stays compact) and the unmatched badge.
export const AccountOptionLabel = ({ label, counts, context }) => {
    const lastUpload = formatDay(counts?.last_uploaded_at)
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                {context === "menu" && counts && (
                    <span style={{ fontSize: 11, color: "#8a8fa3" }}>
                        {lastUpload ? `Last upload ${lastUpload}` : "Never uploaded"}
                    </span>
                )}
                {counts && (
                    <span
                        style={{
                            background: counts.unmatched > 0 ? "#fff3e0" : "#e6f7ee",
                            color: counts.unmatched > 0 ? "#d97706" : "#1d9a5c",
                            borderRadius: 999,
                            padding: "1px 9px",
                            fontSize: 11,
                            fontWeight: 700,
                        }}
                    >
                        {counts.unmatched} unmatched
                    </span>
                )}
            </span>
        </div>
    )
}

// Full-width line under the account/upload row. Kept OUT of the select's own
// column on purpose: inside it, the column grew taller once an account was
// picked and pushed the file input and buttons out of line.
export const AccountInfoLine = ({ counts }) => {
    if (!counts) return <div className="bsc-account-info" data-testid="account-info">No statement uploaded for this account yet.</div>
    const lastUpload = formatDay(counts.last_uploaded_at)
    const upTo = formatDay(counts.last_statement_date)
    return (
        <div className="bsc-account-info" data-testid="account-info">
            <span>Last uploaded: <b>{lastUpload || "—"}</b></span>
            <span>Statement up to: <b>{upTo || "—"}</b></span>
            <span><b>{counts.unmatched}</b> unmatched</span>
            <span>{counts.matched} matched</span>
            <span>{counts.total} total</span>
        </div>
    )
}

// Words too generic to identify a customer/expense from a bank narration.
const GENERIC_WORDS = new Set([
    "private", "limited", "pvt", "ltd", "llp", "india", "services", "service", "solutions",
    "technologies", "technology", "company", "group", "international", "enterprises",
    "payment", "expense", "charges", "bill", "monthly", "from", "with", "the", "and",
])

const nameAppearsIn = (name, description) => {
    const haystack = String(description || "").toLowerCase()
    if (!haystack) return false
    return String(name || "")
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .some(w => w.length >= 4 && !GENERIC_WORDS.has(w) && haystack.includes(w))
}

// Builds react-select grouped options: a short "Suggested" group first —
// records whose INR amount is closest to the statement row, or whose name
// appears in its description — followed by the full list. Returns the flat
// list unchanged when nothing qualifies.
//   candidates : options to rank (each needs cmpInr; optional nameText)
//   allOptions : the full list shown underneath
export const buildSuggestedGroups = (candidates, allOptions, entryAmount, description, { tolerance = 0.1, max = 6, allLabel = "All" } = {}) => {
    const amount = Number(entryAmount) || 0
    const ranked = []
    for (const o of candidates || []) {
        const diff = Number.isFinite(o.cmpInr) && amount > 0 ? Math.abs(o.cmpInr - amount) : Infinity
        const exact = diff < 1
        const close = !exact && diff / amount <= tolerance
        const named = nameAppearsIn(o.nameText, description)
        if (!exact && !close && !named) continue
        const rank = exact ? 0 : close && named ? 1 : close ? 2 : 3
        const tag = exact ? "Exact amount" : close ? "Close amount" : "Name match"
        ranked.push({ rank, diff, option: { ...o, label: o.suggestLabel || o.label, tag } })
    }
    if (!ranked.length) return allOptions || []
    ranked.sort((a, b) => a.rank - b.rank || a.diff - b.diff)
    return [
        { label: "Suggested for this entry", options: ranked.slice(0, max).map(r => r.option) },
        { label: allLabel, options: allOptions || [] },
    ]
}

// Option renderer for the grouped dropdowns above — plain label plus the
// "Exact amount / Close amount / Name match" tag on suggested rows.
export const formatMatchOption = option => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>{option.label}</span>
        {option.tag && <span className={`bsc-opt-tag ${option.tag === "Exact amount" ? "exact" : ""}`}>{option.tag}</span>}
    </div>
)

// Registered-expense row (bankStatement|creditCardStatement /expense/options)
// -> dropdown option.
export const toExpenseOption = e => {
    const total = Number(e.amount) || 0
    const due = total - (Number(e.tbal) || 0)
    const details = [e.category_name, formatDay(e.date)].filter(Boolean).join(", ")
    return {
        value: e.id,
        due,
        // A fully paid expense is a Map candidate — compare its full amount.
        cmpInr: Math.abs(due) > 0.01 ? due : total,
        nameText: e.title,
        label: `${e.title}${details ? ` — ${details}` : ""} (Due: ${formatInr(due)} of ${formatInr(total)})`,
    }
}
